import React from "react";
import { Link } from "react-router-dom";
import { PaEmptyState, PaPagination, PaSkeleton } from "components/platform-admin";
import {
  buildTenantTableColumns,
  formatAiUsageAud,
  formatAiUsageCount,
  formatCareSector,
} from "./aiUsageOverview.utils";

const SortIndicator = ({ active, direction }) => {
  if (!active) {
    return <span className="ml-1 text-gray-300">↕</span>;
  }

  return <span className="ml-1 text-indigo-600">{direction === "asc" ? "↑" : "↓"}</span>;
};

const AiUsageTenantTable = ({
  rows = [],
  loading = false,
  sort = "spent_aud",
  direction = "desc",
  onSortChange,
  page = 1,
  totalPages = 1,
  total = 0,
  perPage = 25,
  onPageChange,
}) => {
  const columns = buildTenantTableColumns();

  const handleSort = (column) => {
    if (!column.sortable || !onSortChange) {
      return;
    }

    if (sort === column.key) {
      onSortChange(column.key, direction === "asc" ? "desc" : "asc");
      return;
    }

    onSortChange(column.key, column.key === "name" ? "asc" : "desc");
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">Tenant leaderboard</h2>
        <p className="text-xs text-gray-500">Sortable AI spend and call volume by tenant</p>
      </div>

      {loading ? (
        <div className="p-6">
          <PaSkeleton variant="table" lines={6} />
        </div>
      ) : rows.length === 0 ? (
        <div className="p-6">
          <PaEmptyState title="No tenants match" message="Try a different search or date range." />
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 font-semibold text-gray-700 ${
                    column.align === "right"
                      ? "text-right"
                      : column.align === "center"
                        ? "text-center"
                        : "text-left"
                  }`}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column)}
                      className="inline-flex items-center hover:text-indigo-600"
                    >
                      {column.label}
                      <SortIndicator active={sort === column.key} direction={direction} />
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.sp_id} className="hover:bg-indigo-50/40">
                <td className="px-4 py-3">
                  <Link
                    to={`/tenants/${row.sp_id}?tab=ai-usage`}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {row.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-gray-600">{formatCareSector(row.care_sector)}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">
                  {formatAiUsageAud(row.spent_aud)}
                </td>
                <td className="px-4 py-3 text-right text-gray-700">
                  {formatAiUsageCount(row.call_count)}
                </td>
                <td className="px-4 py-3 text-right text-gray-700">
                  {formatAiUsageAud(row.balance_aud)}
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                      row.ai_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {row.ai_enabled ? "On" : "Off"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PaPagination
        page={page}
        totalPages={totalPages}
        total={total}
        rows={rows}
        perPage={perPage}
        loading={loading}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default AiUsageTenantTable;
